/**
 * Assets Table
 *
 * Full sortable list of assets with their period emissions. Replaces the
 * top-N "Top Emitting Assets" card from the old Insights → Assets tab.
 * Includes a Type filter and a stacked Scope 1/2/3 contribution bar.
 */

import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { getAssetTypeBrand, ASSET_TYPE_BRANDING } from '../../config/assetTypeBranding';
import { useEmissionsFormatter } from '../../hooks/useEmissionsFormatter';
import { SCOPE_COLORS } from '../../config/scopeBranding';
import { AssetType, type EmissionAsset, type AssetComparisonItem } from '../../types/emissions';

type SortKey = 'rank' | 'name' | 'type' | 'activities' | 'units' | 'intensity' | 'percent' | 'co2e';
type SortDir = 'asc' | 'desc';

type TypeFilter = AssetType | 'all';

const SCOPE_KEYS = ['scope1', 'scope2', 'scope3'] as const;
type ScopeKey = (typeof SCOPE_KEYS)[number];

const SCOPE_LABELS: Record<ScopeKey, string> = {
  scope1: 'Scope 1',
  scope2: 'Scope 2',
  scope3: 'Scope 3',
};

interface AssetsTableProps {
  assets: EmissionAsset[];
  comparison: AssetComparisonItem[];
  orgTotalCo2e: number;
  onAssetClick: (assetId: string) => void;
}

interface AssetRow {
  _id: string;
  name: string;
  assetType: string;
  totalCo2e: number;
  totalActivities: number;
  unitCount: number;
  /** kg CO₂e per unit; null when unitCount is unset (single-unit asset). */
  intensityPerUnit: number | null;
  scopeBreakdown: Record<ScopeKey, number>;
}

const compareNumbers = (a: number, b: number) => a - b;
const compareStrings = (a: string, b: string) => a.localeCompare(b);

const ASSET_TYPE_OPTIONS = Object.values(AssetType);

export const AssetsTable = ({ assets, comparison, orgTotalCo2e, onAssetClick }: AssetsTableProps) => {
  const { formatCompact } = useEmissionsFormatter();
  const [sortKey, setSortKey] = useState<SortKey>('co2e');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  const rows = useMemo<AssetRow[]>(() => {
    const comparisonById = new Map(comparison.map((c) => [c._id, c]));
    return assets.map((a) => {
      const c = comparisonById.get(a._id);
      const scopeBreakdown: Record<ScopeKey, number> = { scope1: 0, scope2: 0, scope3: 0 };
      for (const s of c?.scopes ?? []) {
        if (s.scope === 'scope1' || s.scope === 'scope2' || s.scope === 'scope3') {
          scopeBreakdown[s.scope] += s.co2e;
        }
      }
      const unitCount = a.unitCount ?? 1;
      const totalCo2e = c?.totalCo2e ?? 0;
      // Only show per-unit intensity for grouped assets; single-unit assets
      // would just duplicate the CO₂e column.
      const intensityPerUnit = a.unitCount && a.unitCount > 1 ? totalCo2e / a.unitCount : null;
      return {
        _id: a._id,
        name: a.name,
        assetType: a.assetType,
        totalCo2e,
        totalActivities: c?.totalActivities ?? a.activityCount ?? 0,
        unitCount,
        intensityPerUnit,
        scopeBreakdown,
      };
    });
  }, [assets, comparison]);

  // Disable type-filter buttons for types with no rows so users can't pick an empty filter.
  const presentTypes = useMemo(() => new Set(rows.map((r) => r.assetType as AssetType)), [rows]);

  const filteredRows = useMemo(
    () => (typeFilter === 'all' ? rows : rows.filter((r) => r.assetType === typeFilter)),
    [rows, typeFilter],
  );

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows].sort((a, b) => {
      switch (sortKey) {
        case 'name':
          return compareStrings(a.name, b.name);
        case 'type':
          return compareStrings(getAssetTypeBrand(a.assetType).label, getAssetTypeBrand(b.assetType).label);
        case 'activities':
          return compareNumbers(a.totalActivities, b.totalActivities);
        case 'units':
          return compareNumbers(a.unitCount, b.unitCount);
        case 'intensity':
          // Nulls (single-unit assets) sort to the bottom regardless of dir.
          return compareNumbers(a.intensityPerUnit ?? -Infinity, b.intensityPerUnit ?? -Infinity);
        case 'percent':
        case 'co2e':
          return compareNumbers(a.totalCo2e, b.totalCo2e);
        case 'rank':
        default:
          return compareNumbers(b.totalCo2e, a.totalCo2e);
      }
    });
    return sortDir === 'asc' ? sorted : sorted.reverse();
  }, [filteredRows, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Numeric columns default to desc (largest first), text columns to asc.
      setSortDir(key === 'name' || key === 'type' ? 'asc' : 'desc');
    }
  };

  if (rows.length === 0) {
    return (
      <p className='tw:text-sm tw:text-muted-foreground tw:text-center tw:py-8 tw:mb-0'>
        No assets registered for this period.
      </p>
    );
  }

  return (
    <div className='tw:space-y-4'>
      {/* Type filter — pill row */}
      <div className='tw:flex tw:flex-wrap tw:gap-2 tw:items-center'>
        <span className='tw:text-xs tw:font-medium tw:text-muted-foreground tw:mr-1'>Type:</span>
        <FilterPill
          label='All'
          active={typeFilter === 'all'}
          onClick={() => setTypeFilter('all')}
        />
        {ASSET_TYPE_OPTIONS.map((type) => {
          const brand = ASSET_TYPE_BRANDING[type];
          const disabled = !presentTypes.has(type);
          return (
            <FilterPill
              key={type}
              label={brand.label}
              icon={<brand.Icon className={cn('tw:size-3.5', brand.iconColor)} />}
              active={typeFilter === type}
              disabled={disabled}
              onClick={() => setTypeFilter(type)}
            />
          );
        })}
      </div>

      <div className='tw:overflow-x-auto'>
        <table className='tw:w-full tw:text-sm'>
          <thead>
            <tr className='tw:text-xs tw:text-muted-foreground tw:border-b tw:border-border'>
              <th className='tw:text-left tw:pb-2 tw:pt-1 tw:font-medium tw:w-8'>#</th>
              <SortHeader label='Name' sortKey='name' active={sortKey} dir={sortDir} onSort={handleSort} />
              <SortHeader label='Type' sortKey='type' active={sortKey} dir={sortDir} onSort={handleSort} className='tw:w-32' />
              <SortHeader
                label='Activities'
                sortKey='activities'
                active={sortKey}
                dir={sortDir}
                onSort={handleSort}
                align='right'
                className='tw:w-24'
              />
              <SortHeader
                label='Units'
                sortKey='units'
                active={sortKey}
                dir={sortDir}
                onSort={handleSort}
                align='right'
                className='tw:w-20'
              />
              <SortHeader
                label='Intensity / Unit'
                sortKey='intensity'
                active={sortKey}
                dir={sortDir}
                onSort={handleSort}
                align='right'
                className='tw:w-28'
              />
              <SortHeader
                label='% of Org'
                sortKey='percent'
                active={sortKey}
                dir={sortDir}
                onSort={handleSort}
                align='right'
                className='tw:w-20'
              />
              <th className='tw:pb-2 tw:pt-1 tw:font-medium tw:w-40 tw:text-left'>Scope split</th>
              <SortHeader
                label='CO₂e'
                sortKey='co2e'
                active={sortKey}
                dir={sortDir}
                onSort={handleSort}
                align='right'
                className='tw:w-24'
              />
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, i) => {
              const brand = getAssetTypeBrand(row.assetType);
              const pct = orgTotalCo2e > 0 ? (row.totalCo2e / orgTotalCo2e) * 100 : 0;
              const formatted = formatCompact(row.totalCo2e);
              return (
                <tr
                  key={row._id}
                  className='tw:cursor-pointer tw:hover:bg-muted/50 tw:border-b tw:border-border'
                  onClick={() => onAssetClick(row._id)}
                >
                  <td className='tw:py-2 tw:text-muted-foreground'>{i + 1}.</td>
                  <td className='tw:py-2 tw:max-w-[260px]'>
                    <div className='tw:flex tw:items-center tw:gap-2 tw:min-w-0'>
                      <span
                        className={cn(
                          'tw:rounded-full tw:flex tw:items-center tw:justify-center tw:size-6 tw:shrink-0',
                          brand.iconBg,
                        )}
                      >
                        <brand.Icon className={cn('tw:size-3.5', brand.iconColor)} />
                      </span>
                      <span className='tw:truncate'>{row.name}</span>
                    </div>
                  </td>
                  <td className='tw:py-2'>
                    <Badge variant='outline' className={cn('tw:text-xs', brand.iconColor)}>
                      {brand.label}
                    </Badge>
                  </td>
                  <td className='tw:py-2 tw:text-right tw:text-muted-foreground'>{row.totalActivities}</td>
                  <td className='tw:py-2 tw:text-right tw:text-muted-foreground'>
                    {row.unitCount > 1 ? row.unitCount : <span className='tw:text-muted-foreground/50'>—</span>}
                  </td>
                  <td className='tw:py-2 tw:text-right tw:text-muted-foreground'>
                    {row.intensityPerUnit !== null ? (
                      (() => {
                        const f = formatCompact(row.intensityPerUnit);
                        return <span>{f.formatted} {f.unitCompact}<span className='tw:text-muted-foreground/70'>/unit</span></span>;
                      })()
                    ) : (
                      <span className='tw:text-muted-foreground/50'>—</span>
                    )}
                  </td>
                  <td className='tw:py-2 tw:text-right tw:text-xs tw:text-muted-foreground'>{pct.toFixed(1)}%</td>
                  <td className='tw:py-2 tw:pr-2'>
                    <ScopeStackedBar breakdown={row.scopeBreakdown} orgTotalCo2e={orgTotalCo2e} />
                  </td>
                  <td className='tw:py-2 tw:text-right'>{formatted.display}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {sortedRows.length === 0 && typeFilter !== 'all' && (
          <p className='tw:text-sm tw:text-muted-foreground tw:text-center tw:py-6 tw:mb-0'>
            No assets match this type filter.
          </p>
        )}
      </div>
    </div>
  );
};

interface FilterPillProps {
  label: string;
  active: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  onClick: () => void;
}

const FilterPill = ({ label, active, disabled, icon, onClick }: FilterPillProps) => (
  <button
    type='button'
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'tw:inline-flex tw:items-center tw:gap-1.5 tw:px-2.5 tw:py-1 tw:rounded-full tw:text-xs tw:font-medium tw:border tw:transition-colors',
      active
        ? 'tw:bg-blue-50 tw:border-blue-500 tw:text-blue-700'
        : 'tw:bg-background tw:border-border tw:text-muted-foreground tw:hover:bg-muted/50',
      disabled && 'tw:opacity-40 tw:cursor-not-allowed tw:hover:bg-background',
    )}
  >
    {icon}
    {label}
  </button>
);

interface ScopeStackedBarProps {
  breakdown: Record<ScopeKey, number>;
  orgTotalCo2e: number;
}

/**
 * Horizontal stacked bar showing each scope's contribution to the org total
 * for this asset's row. Bar widths are proportional to org total, so two
 * assets with the same emissions render identical bars; an asset with zero
 * emissions renders empty. Each segment carries a tooltip with its scope
 * label and value.
 */
const ScopeStackedBar = ({ breakdown, orgTotalCo2e }: ScopeStackedBarProps) => {
  const { formatCompact } = useEmissionsFormatter();
  const total = breakdown.scope1 + breakdown.scope2 + breakdown.scope3;
  if (total === 0 || orgTotalCo2e === 0) {
    return <div className='tw:h-2 tw:rounded-full tw:bg-muted' aria-hidden />;
  }
  const segments = SCOPE_KEYS.filter((k) => breakdown[k] > 0).map((k) => ({
    key: k,
    width: (breakdown[k] / orgTotalCo2e) * 100,
    color: SCOPE_COLORS[k],
    title: `${SCOPE_LABELS[k]} · ${formatCompact(breakdown[k]).display}`,
  }));
  return (
    <div
      className='tw:h-2 tw:rounded-full tw:bg-muted tw:overflow-hidden tw:flex'
      role='img'
      aria-label={`Scope split: ${segments.map((s) => s.title).join(', ')}`}
    >
      {segments.map((s) => (
        <div
          key={s.key}
          title={s.title}
          style={{ width: `${s.width}%`, backgroundColor: s.color }}
          className='tw:h-full'
        />
      ))}
    </div>
  );
};

interface SortHeaderProps {
  label: string;
  sortKey: SortKey;
  active: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
  className?: string;
}

const SortHeader = ({ label, sortKey, active, dir, onSort, align = 'left', className }: SortHeaderProps) => {
  const isActive = active === sortKey;
  const Icon = isActive ? (dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th
      className={cn(
        'tw:pb-2 tw:pt-1 tw:font-medium tw:cursor-pointer tw:select-none tw:hover:text-foreground',
        align === 'right' ? 'tw:text-right' : 'tw:text-left',
        className,
      )}
      onClick={() => onSort(sortKey)}
    >
      <span className={cn('tw:inline-flex tw:items-center tw:gap-1', align === 'right' && 'tw:justify-end')}>
        {label}
        <Icon className={cn('tw:size-3', isActive ? 'tw:text-foreground' : 'tw:text-muted-foreground/50')} />
      </span>
    </th>
  );
};
